import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useRazorpay } from 'react-razorpay';
import { ChevronRight, ChevronDown, Clock, ArrowLeft, Tag, X, Loader2, CalendarDays } from 'lucide-react';
import api from '../../services/api';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { toast } from '../../hooks/use-toast';
import LoginModal from '../OTPLoginModal';
import { regionsOverlapClient } from './regionOverlap';
import { SPORT_LABELS, SPORT_ICONS } from '../../config/venueConfig';
import { RichTextView } from '../RichTextEditor';
import { timeToMin, toMinAnchored } from '../../utils/timeSlots';

const NAVY = '#0A2C93';
const ORANGE = '#F97316';
const HOLD_SECONDS = 120;

const chipFor = (ct) => {
  if (ct === 'full') return '11v11';
  if (ct === 'half') return '9v9';
  if (ct === 'quarter') return '7v7';
  if (ct === '5aside') return '5-aside';
  return ct || 'Other';
};
const orderRank = { full: 0, half: 1, quarter: 2, '5aside': 3 };

// Cross-midnight-aware time arithmetic. Overnight facilities (e.g. 05:00 → 02:00
// next day) put post-midnight slots on TODAY's grid, but naive `t.split(':')`
// minute math breaks contiguity across the 23:30 → 00:00 boundary
// (00:00 = 0 < 23:30 = 1410, gap looks like -1410 not +30). `timeToMin` and
// `toMinAnchored` now live in `utils/timeSlots.js` so grid + BulkBooking
// share ONE copy — no more per-surface drift.
const pickAnchor = (times) => {
  if (!times || times.length === 0) return null;
  const mins = times.map(timeToMin);
  const lo = Math.min(...mins), hi = Math.max(...mins);
  // A single operating day is <=24h. A range >12h between selected slots
  // implies the selection crosses midnight; anchor to the earliest pre-mid
  // slot (smallest value >= 12:00) so post-mid slots roll over cleanly.
  if (hi - lo > 720) {
    const preMids = mins.filter((m) => m >= 720);
    if (preMids.length) return times[mins.indexOf(Math.min(...preMids))];
  }
  return times[mins.indexOf(lo)];
};
// Chronological sort of HH:MM times respecting midnight rollover.
const chronSort = (times) => {
  const a = pickAnchor(times);
  return [...times].sort((x, y) => toMinAnchored(x, a) - toMinAnchored(y, a));
};

export default function BookingGrid() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sportParam = searchParams.get('sport');
  const { Razorpay } = useRazorpay();
  const { user, isAuthenticated } = useCustomerAuth();

  const [facility, setFacility] = useState(null);
  const [sport, setSport] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chip, setChip] = useState('All');
  const [selected, setSelected] = useState(() => new Map()); // key "time|court" -> {time, courtId, region, price}
  const [coupon, setCoupon] = useState('');
  const [applied, setApplied] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [holdLeft, setHoldLeft] = useState(0);
  const [payMode, setPayMode] = useState('full'); // 'full' | 'deposit'
  const [publicCoupons, setPublicCoupons] = useState([]);
  const [pendingProceed, setPendingProceed] = useState(false);
  const [barHeight, setBarHeight] = useState(0);
  const [dateBarH, setDateBarH] = useState(60);
  const dateStripRef = React.useRef(null);
  const [stage, setStage] = useState('grid'); // 'grid' | 'summary'
  const [openPolicy, setOpenPolicy] = useState(null); // 'cancellation' | 'reschedule' | null
  const summaryRef = React.useRef(null);
  const dateInputRef = React.useRef(null);
  const upcomingRowRef = React.useRef(null);
  const headerScrollRef = React.useRef(null);
  const bodyScrollRef = React.useRef(null);
  const syncingRef = React.useRef(false);
  const syncScroll = (from, to) => {
    if (syncingRef.current || !from || !to) return;
    syncingRef.current = true;
    to.scrollLeft = from.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  const pp = facility?.partial_payment;
  const partialEnabled = !!(pp?.enabled && pp?.customer_percentage > 0 && pp?.customer_percentage < 100);
  const maxDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 90); return d.toISOString().slice(0, 10); }, []);
  const todayStr = new Date().toISOString().slice(0, 10);

  // ---- load facility ----
  useEffect(() => {
    (async () => {
      try {
        const facs = await api.getPublicFacilities();
        const f = (facs || []).find((x) => x.id === venueId || x.slug === venueId);
        setFacility(f || null);
        const sports = f?.sports || [];
        if (sports.length === 1) setSport(sports[0]);
        else if (sportParam && sports.includes(sportParam)) setSport(sportParam); // pre-selected from venue picker
        if (f?.id) { try { setPublicCoupons(await api.getPublicCoupons(f.id)); } catch (_) {} }
      } catch (e) { toast({ title: 'Error', description: 'Could not load venue', variant: 'destructive' }); }
    })();
  }, [venueId]);

  // Overnight facilities need a SECOND availability fetch for date+1 so the
  // "Next Day Early Morning" rows reflect the real bookings on the NEXT
  // calendar date. This aligns Playspots with Playo + admin (both store
  // post-midnight bookings under the calendar date they fall on). Without
  // this, a Playo-booked 00:00 slot on 28 Sept would show as available
  // when a Playspots customer views the 27 Sept grid — exact double-book
  // risk the operator flagged.
  const [nextAvail, setNextAvail] = useState(null);
  const nextDateStr = useMemo(() => {
    const d = new Date(`${date}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [date]);
  const isOvernight = useMemo(() => {
    const oh = facility?.operating_hours || {};
    return oh.end && oh.start && oh.end !== '00:00' && oh.end < oh.start;
  }, [facility]);

  // ---- load availability for date (unified engine) ----
  const loadAvail = useCallback(async () => {
    if (!facility) return;
    setLoading(true);
    try {
      const data = await api.getPublicSlotAvailability(date, facility.id);
      setAvail(data);
      // Overnight venue → also fetch date+1 so the "Next Day Early Morning"
      // rows reflect next-day bookings (Playo, admin, or Playspots).
      if (isOvernight) {
        try {
          const nxt = await api.getPublicSlotAvailability(nextDateStr, facility.id);
          setNextAvail(nxt);
        } catch (_) { setNextAvail(null); }
      } else {
        setNextAvail(null);
      }
    } catch (e) { toast({ title: 'Error', description: 'Could not load availability', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [facility, date, isOvernight, nextDateStr]);
  useEffect(() => { loadAvail(); }, [loadAvail]);

  // ---- countdown for active holds ----
  useEffect(() => {
    if (holdLeft <= 0) return;
    const t = setInterval(() => setHoldLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [holdLeft]);

  const sports = facility?.sports || [];
  const times = useMemo(() => {
    // Operating-day chronological order: for overnight facilities (e.g. 05:00 →
    // 02:00 next day), post-midnight slots (00:00, 00:30, 01:00, 01:30) belong
    // AT THE END of today's grid, not at the top. Anchor by operating_hours.start
    // so any time < start rolls over to +1440.
    const raw = Object.keys(avail?.slots || {});
    const opStart = facility?.operating_hours?.start || '06:00';
    const opStartMin = timeToMin(opStart);
    const key = (t) => {
      const m = timeToMin(t);
      return m < opStartMin ? m + 1440 : m;
    };
    return raw.sort((a, b) => key(a) - key(b));
  }, [avail, facility]);
  // First bookable (non-past) slot on TODAY — used to auto-scroll the grid to the upcoming slots.
  const firstUpcoming = useMemo(() => {
    if (date !== todayStr) return null;
    const now = `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
    return times.find((t) => t > now) || null;
  }, [times, date, todayStr]);

  // columns = courts for chosen sport, ordered largest→smallest
  const columns = useMemo(() => {
    const cm = (avail?.courts || []).filter((c) => !sport || c.sport === sport);
    return [...cm].sort((a, b) =>
      (orderRank[a.court_type] ?? 9) - (orderRank[b.court_type] ?? 9) ||
      (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [avail, sport]);

  const chips = useMemo(() => ['All', ...Array.from(new Set(columns.map((c) => chipFor(c.court_type))))], [columns]);
  const visibleCols = useMemo(
    () => (chip === 'All' ? columns : columns.filter((c) => chipFor(c.court_type) === chip)),
    [columns, chip]);

  // active format lock: a checkout maps to ONE court (multiple times). Track the locked court.
  const lockedCourt = useMemo(() => {
    const first = [...selected.values()][0];
    return first?.courtId || null;
  }, [selected]);

  const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
  const isPast = (time) => {
    // A slot is "past" only for TODAY's grid at or before the current wall clock.
    // For overnight facilities (e.g. 05:00 → 02:00 next day), the post-midnight
    // slots (00:00-01:30) shown on today's grid are actually tomorrow's early
    // hours — they must NEVER be marked past when viewing today, regardless of
    // the current time being e.g. 20:00.
    if (date !== new Date().toISOString().slice(0, 10)) return false;
    const oh = facility?.operating_hours || { start: '06:00', end: '23:00' };
    const overnight = oh.end && oh.start && oh.end !== '00:00' && oh.end < oh.start;
    if (overnight && time < oh.start) return false;  // post-midnight next-day slot
    return time <= nowHHMM();
  };

  // When overnight, slots whose clock time is before op_start are the
  // NEXT calendar day. Read their availability from `nextAvail` (fetched
  // for date+1) so Playo / admin bookings on D+1 show as booked.
  const opStartForNext = facility?.operating_hours?.start || '06:00';
  const isNextDaySlot = (time) => isOvernight && time < opStartForNext;
  const availFor = (time) => (isNextDaySlot(time) ? nextAvail : avail);
  const dateFor = (time) => (isNextDaySlot(time) ? nextDateStr : date);

  const cellState = (time, court) => {
    const src = availFor(time);
    const sec = src?.slots?.[time]?.sections?.[court.id];
    if (!sec) return { state: 'na', price: 0 };
    const price = (sec.price || 0) / 2; // section price is HOURLY; grid rows are 30-min slots
    const key = `${time}|${court.id}`;
    // Past slots are UNIFORM: no state leaked (booked vs empty), no price. Checked first.
    if (isPast(time)) return { state: 'past', price: 0 };
    if (selected.has(key)) return { state: 'selected', price };
    if (!sec.available) return { state: 'booked', price };
    // client overlap greying vs current selection — SAME TIME ROW ONLY
    for (const s of selected.values()) {
      if (s.time === time && s.courtId !== court.id && regionsOverlapClient(s.region, sec.region)) {
        return { state: 'overlap', price };
      }
    }
    return { state: 'available', price };
  };

  const toggle = (time, court) => {
    const src = availFor(time);
    const sec = src?.slots?.[time]?.sections?.[court.id];
    if (!sec || !sec.available || isPast(time)) return;
    const price = (sec.price || 0) / 2;
    const key = `${time}|${court.id}`;
    setSelected((prev) => {
      const n = new Map(prev);
      if (n.has(key)) { n.delete(key); return n; }
      // one court per checkout (clean single-order path). Switching court resets selection.
      if (lockedCourt && lockedCourt !== court.id) {
        n.clear();
        toast({ title: 'Switched format', description: 'Selecting a different format starts a fresh selection.' });
      }
      // block a cell that overlaps an existing selection AT THE SAME TIME
      for (const s of n.values()) {
        if (s.time === time && s.courtId !== court.id && regionsOverlapClient(s.region, sec.region)) return prev;
      }
      n.set(key, { time, courtId: court.id, region: sec.region, price, courtName: court.name });
      // enforce contiguity: one court + one consecutive run per checkout (single order).
      // Uses anchored minutes so a 23:00→00:30 cross-midnight run reads as contiguous.
      const ts = [...n.values()].map((s) => s.time);
      const anchor = pickAnchor(ts);
      const sorted = [...ts].sort((x, y) => toMinAnchored(x, anchor) - toMinAnchored(y, anchor));
      for (let i = 1; i < sorted.length; i++) {
        if (toMinAnchored(sorted[i], anchor) - toMinAnchored(sorted[i - 1], anchor) !== 30) {
          toast({ title: 'Select consecutive slots', description: 'Pick back-to-back times in one format for a single booking.' });
          return prev;
        }
      }
      return n;
    });
  };

  const selectedList = useMemo(() => {
    // Chronological ordering — a 23:00→00:30 cross-midnight run must render
    // (and sum) in real-time order, not lexical (which would put 00:00 first).
    const arr = [...selected.values()];
    const anchor = pickAnchor(arr.map((s) => s.time));
    return arr.sort((a, b) => toMinAnchored(a.time, anchor) - toMinAnchored(b.time, anchor));
  }, [selected]);
  const subtotal = selectedList.reduce((s, x) => s + (x.price || 0), 0);
  const total = applied ? Math.max(0, subtotal - (applied.discount_amount || 0)) : subtotal;
  const depositAmt = partialEnabled ? Math.round(total * pp.customer_percentage) / 100 : total; // matches server round(amount*pct/100, 2)
  const fmtINR = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 });
  const mmss = `${Math.floor(holdLeft / 60)}:${String(holdLeft % 60).padStart(2, '0')}`;

  const applyCode = async (code) => {
    const c = (code || '').trim().toUpperCase();
    if (!c) return;
    if (subtotal <= 0) { toast({ title: 'Select slots first', description: 'Add slots before applying a coupon.' }); return; }
    try {
      const r = await api.validateCoupon(c, facility.id, subtotal);
      if (r && r.valid === false) { toast({ title: 'Invalid coupon', description: r.message || 'Try another code', variant: 'destructive' }); return; }
      setApplied({ ...r, code: c }); setCoupon(c);
      toast({ title: 'Coupon applied', description: `You saved ₹${r.discount_amount}` });
    } catch (e) { toast({ title: 'Invalid coupon', description: e.message || 'Try another code', variant: 'destructive' }); }
  };
  const applyCoupon = () => applyCode(coupon);

  const endTimeOf = (startTimes) => {
    // Pick chronologically LAST slot (respecting midnight rollover) and add 30.
    const sorted = chronSort([...startTimes]);
    const last = sorted[sorted.length - 1];
    let [h, m] = last.split(':').map(Number); m += 30; if (m >= 60) { m -= 60; h += 1; } if (h >= 24) h = 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Payment not completed (failed/abandoned): stay on the grid, refresh availability.
  // The unpaid hold auto-releases server-side after 2 min — no My Bookings, no countdown nag.
  const backToGrid = async () => {
    setSubmitting(false);
    setHoldLeft(0);
    setSelected(new Map());
    setPayMode('full');
    toast({ title: 'Payment not completed', description: 'No worries — pick your slots again.' });
    try { setAvail(await api.getPublicSlotAvailability(date, facility.id)); } catch (_) {}
  };

  const proceed = async () => {
    if (!isAuthenticated) { setPendingProceed(true); setShowLogin(true); return; }
    if (selectedList.length === 0) return;
    if (selectedList.length < 2) {
      toast({ title: 'Minimum booking is 1 hour', description: 'Please select at least 2 consecutive 30-min slots to continue.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      // Concurrency guard: refresh availability and confirm every selected cell is still free.
      // Fetch both current and next-day for overnight venues so post-midnight slot checks
      // read from the correct source.
      const fresh = await api.getPublicSlotAvailability(date, facility.id);
      const freshNext = isOvernight
        ? await api.getPublicSlotAvailability(nextDateStr, facility.id).catch(() => null)
        : null;
      const sourceFor = (time) => (isNextDaySlot(time) ? freshNext : fresh);
      const taken = selectedList.filter((s) => !sourceFor(s.time)?.slots?.[s.time]?.sections?.[s.courtId]?.available);
      if (taken.length) {
        setAvail(fresh);
        if (freshNext !== null) setNextAvail(freshNext);
        setSelected((prev) => { const n = new Map(prev); taken.forEach((t) => n.delete(`${t.time}|${t.courtId}`)); return n; });
        toast({ title: 'That slot was just taken', description: `${taken.map((t) => t.time).join(', ')} is no longer available. We removed it — please review and retry.`, variant: 'destructive' });
        setSubmitting(false);
        return;
      }
      const times = selectedList.map((s) => s.time);
      // Cross-midnight-aware contiguity + start_time. selectedList is already
      // chronologically sorted, but we recompute the anchor here so the guard
      // is self-contained and defensive against any future reorder.
      const anchor = pickAnchor(times);
      const sortedT = [...times].sort((x, y) => toMinAnchored(x, anchor) - toMinAnchored(y, anchor));
      for (let i = 1; i < sortedT.length; i++) {
        if (toMinAnchored(sortedT[i], anchor) - toMinAnchored(sortedT[i - 1], anchor) !== 30) {
          toast({ title: 'Select consecutive slots', description: 'Please pick back-to-back times before proceeding.', variant: 'destructive' });
          setSubmitting(false); return;
        }
      }
      const court = columns.find((c) => c.id === lockedCourt);
      // Calendar-date alignment: if the first (chronologically earliest)
      // slot is post-midnight on an overnight venue, the booking's
      // calendar date is the NEXT day — matches Playo + admin convention.
      // Mixed selections (23:00-01:00) stay on the current date (first
      // slot is 23:00, pre-midnight) and rely on backend's existing
      // cross-midnight bleed logic for availability.
      const bookingDate = isNextDaySlot(sortedT[0]) ? nextDateStr : date;
      const bookingData = {
        facility_id: facility.id, date: bookingDate,
        start_time: sortedT[0], end_time: endTimeOf(times),
        slot_type: court?.court_type || sport, court_id: lockedCourt, sport,
        customer_name: user?.name || 'Guest', customer_email: user?.email || '', customer_phone: user?.phone || '',
        status: 'pending', amount: total, amount_paid: 0, payment_status: 'pending',
        source: 'online', booking_type: 'b2c',
        discount_percent: applied?.discount_percent || null, discount_amount: applied?.discount_amount || null,
        coupon_code: applied?.code || null, coupon_id: applied?.coupon_id || null,
      };
      const result = await api.createCustomerBooking(bookingData); // places 2-min hold + SEC-004 server price
      const bookingId = result.id || result.booking_id;
      setHoldLeft(HOLD_SECONDS); // live countdown on held slots
      if (!total || total <= 0) { toast({ title: 'Booking confirmed!' }); navigate('/my-bookings', { state: { refresh: Date.now() } }); return; }
      if (!Razorpay) { toast({ title: 'Payment loading', description: 'Try again in a moment', variant: 'destructive' }); setSubmitting(false); return; }
      const order = await api.createPublicPaymentOrder(bookingId, total, partialEnabled ? payMode : 'full'); // server amount + server-computed deposit
      const rzp = new Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: facility?.name || 'PlaySpaces', description: `${sport} · ${date}`, order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        theme: { color: NAVY },
        handler: async (resp) => {
          try {
            await api.verifyPublicPayment({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature, booking_id: bookingId });
            toast({ title: 'Payment successful!', description: 'Your booking is confirmed.' });
            navigate('/my-bookings', { state: { refresh: Date.now() } });
          } catch (e) { toast({ title: 'Payment verification failed', description: e.message || 'Please try again', variant: 'destructive' }); await backToGrid(); }
          finally { setSubmitting(false); }
        },
        modal: { ondismiss: () => { backToGrid(); } },
      });
      rzp.on('payment.failed', () => { backToGrid(); });
      rzp.open();
    } catch (e) {
      toast({ title: 'Could not proceed', description: e.message || 'Please try again', variant: 'destructive' });
      setSubmitting(false);
    }
  };

  // Resume checkout after a successful customer OTP login (avoids stale-closure re-login loop).
  useEffect(() => {
    if (isAuthenticated && pendingProceed && !showLogin) {
      setPendingProceed(false);
      proceed();
    }
  }, [isAuthenticated, pendingProceed, showLogin]);

  // On TODAY, auto-scroll the grid so the current/next upcoming slot is in view (skip past morning hours).
  useEffect(() => {
    if (loading || !firstUpcoming) return;
    const t = setTimeout(() => {
      const el = upcomingRowRef.current;
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
  }, [loading, firstUpcoming, sport]);

  // Keep the page's bottom padding equal to the docked checkout panel's height, so the
  // grid always scrolls FULLY above it and the last slot is never hidden behind the panel.
  useEffect(() => {
    const el = summaryRef.current;
    if (!el) { setBarHeight(0); return; }
    const update = () => setBarHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedList.length, payMode, applied, publicCoupons.length, partialEnabled]);

  // Measure the sticky date bar so the column-header bar can pin directly beneath it.
  useEffect(() => {
    const el = dateStripRef.current;
    if (!el) return;
    const update = () => setDateBarH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sport, loading]);

  if (!facility) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const slotEnd = (t) => { let [h, m] = t.split(':').map(Number); m += 30; if (m >= 60) { m -= 60; h += 1; } if (h >= 24) h = 0; return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`; };
  const fmtDate = (ds) => { try { return new Date(ds + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return ds; } };
  const goToSummary = () => {
    if (selectedList.length < 2) { toast({ title: 'Minimum booking is 1 hour', description: 'Please select at least 2 consecutive 30-min slots.', variant: 'destructive' }); return; }
    setStage('summary');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };
  const removeSlot = (time, courtId) => setSelected((p) => { const n = new Map(p); n.delete(`${time}|${courtId}`); return n; });

  // ============ BOOKING SUMMARY / REVIEW PAGE ============
  if (stage === 'summary') {
    return (
      <div className="min-h-screen bg-slate-50" style={{ paddingBottom: barHeight + 24 }}>
        <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <button data-testid="summary-back-btn" onClick={() => setStage('grid')} className="p-1 -ml-1"><ArrowLeft className="h-5 w-5" style={{ color: NAVY }} /></button>
            <div>
              <p className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>{facility.name}</p>
              <p className="text-[11px] text-slate-500 capitalize">{SPORT_LABELS[sport] || sport} · Booking summary</p>
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
          {/* Terms & Conditions */}
          <section className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="summary-terms">
            <h2 className="font-heading font-bold text-sm mb-2" style={{ color: NAVY }}>Terms &amp; Conditions</h2>
            <RichTextView html={facility.terms_conditions} />
          </section>

          {/* Your slots */}
          <section className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="summary-slots">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-bold text-sm" style={{ color: NAVY }}>Your slots</h2>
              <button data-testid="add-more-slots" onClick={() => setStage('grid')} className="text-xs font-semibold" style={{ color: ORANGE }}>+ Add more slots</button>
            </div>
            <div className="space-y-2">
              {selectedList.map((s) => (
                <div key={`${s.time}|${s.courtId}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-50 last:border-0" data-testid={`summary-slot-${s.time}-${s.courtId}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-900 truncate">{s.courtName}</p>
                    <p className="text-[11px] text-slate-500">{fmtDate(date)} · {s.time}–{slotEnd(s.time)}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-semibold text-sm text-slate-900">₹{s.price?.toLocaleString('en-IN')}</span>
                    <button data-testid={`summary-remove-${s.time}-${s.courtId}`} onClick={() => removeSlot(s.time, s.courtId)} className="p-1 rounded-full hover:bg-slate-100"><X className="h-4 w-4 text-slate-400" /></button>
                  </div>
                </div>
              ))}
            </div>
            {selectedList.length < 2 && (
              <p className="text-[11px] text-orange-600 mt-2 font-medium" data-testid="summary-min-hint">Minimum booking is 1 hour — add at least 2 consecutive slots.</p>
            )}
          </section>

          {/* Coupon */}
          <section className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="summary-coupon">
            <h2 className="font-heading font-bold text-sm mb-2" style={{ color: NAVY }}>Have a coupon?</h2>
            {publicCoupons.length > 0 && !applied && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2" data-testid="public-coupons">
                {publicCoupons.map((pc) => (
                  <button key={pc.code} data-testid={`public-coupon-${pc.code}`} onClick={() => applyCode(pc.code)}
                    className="shrink-0 flex items-center gap-1 text-[11px] font-semibold rounded-full border border-dashed px-2.5 py-1" style={{ borderColor: ORANGE, color: ORANGE }}>
                    <Tag className="h-3 w-3" /> {pc.code} · {pc.discount_percent}% OFF
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Tag className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code"
                  className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm uppercase" disabled={!!applied} />
              </div>
              {applied
                ? <button data-testid="coupon-remove" onClick={() => { setApplied(null); setCoupon(''); }} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">Remove</button>
                : <button data-testid="coupon-apply" onClick={applyCoupon} className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: NAVY }}>Apply</button>}
            </div>
            {applied && <p className="text-[11px] text-green-600 mt-1.5 font-medium">Coupon {applied.code} applied — you saved ₹{applied.discount_amount}</p>}
          </section>

          {/* Venue policies */}
          <section className="bg-white rounded-2xl border border-slate-100 p-2" data-testid="summary-policies">
            {[['cancellation', 'Cancellation Policy', facility.cancellation_policy], ['reschedule', 'Reschedule Policy', facility.reschedule_policy]].map(([key, label, html]) => (
              <div key={key} className="border-b border-slate-50 last:border-0">
                <button data-testid={`policy-toggle-${key}`} onClick={() => setOpenPolicy(openPolicy === key ? null : key)}
                  className="w-full flex items-center justify-between px-2 py-3 text-left">
                  <span className="font-heading font-semibold text-sm" style={{ color: NAVY }}>{label}</span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openPolicy === key ? 'rotate-180' : ''}`} />
                </button>
                {openPolicy === key && <div className="px-2 pb-3" data-testid={`policy-body-${key}`}><RichTextView html={html} /></div>}
              </div>
            ))}
          </section>
        </div>

        {/* Docked pay bar */}
        <div ref={summaryRef} className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40" data-testid="summary-pay-bar">
          <div className="max-w-2xl mx-auto px-4 py-3">
            {holdLeft > 0 && (
              <div className="mb-2 text-[11px] font-semibold flex items-center gap-1" style={{ color: ORANGE }} data-testid="hold-countdown"><Clock className="h-3.5 w-3.5" /> Slots held — {mmss} left to pay</div>
            )}
            {partialEnabled && (
              <div className="grid grid-cols-2 gap-2 mb-2" data-testid="partial-payment-options">
                <button type="button" data-testid="pay-full-option" onClick={() => setPayMode('full')}
                  className={`text-left p-2.5 rounded-lg border-2 ${payMode === 'full' ? 'border-[#0A2C93] bg-[#0A2C93]/5' : 'border-slate-200'}`}>
                  <p className="text-[11px] font-semibold text-slate-700">Pay Full</p>
                  <p className="text-sm font-bold text-slate-900">₹{fmtINR(total)} now</p>
                  <p className="text-[10px] text-slate-400">You're all set · No extra fees</p>
                </button>
                <button type="button" data-testid="pay-deposit-option" onClick={() => setPayMode('deposit')}
                  className={`text-left p-2.5 rounded-lg border-2 ${payMode === 'deposit' ? 'border-[#0A2C93] bg-[#0A2C93]/5' : 'border-slate-200'}`}>
                  <p className="text-[11px] font-semibold text-slate-700">Partial Pay</p>
                  <p className="text-sm font-bold text-slate-900">₹{fmtINR(depositAmt)} now</p>
                  <p className="text-[10px] text-slate-400">₹{fmtINR(total - depositAmt)} at venue · No extra fees</p>
                </button>
              </div>
            )}
            <button data-testid="summary-proceed-btn" onClick={proceed} disabled={submitting || selectedList.length < 2}
              className="w-full text-white font-bold py-3 rounded-xl text-sm disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: ORANGE }}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Proceed to pay ₹{partialEnabled && payMode === 'deposit' ? fmtINR(depositAmt) : fmtINR(total)}
            </button>
          </div>
        </div>

        {showLogin && <LoginModal onClose={() => { setShowLogin(false); setPendingProceed(false); }} onSuccess={() => { setShowLogin(false); }} />}
      </div>
    );
  }

  const cellCls = (state, sel) => {
    if (sel) return 'text-white font-bold';
    if (state === 'available') return 'bg-white border border-slate-200 hover:border-[#0A2C93]/50 text-slate-800 cursor-pointer';
    if (state === 'overlap') return 'bg-slate-50 text-slate-300 cursor-not-allowed';
    return 'bg-slate-100 text-slate-400 cursor-not-allowed';
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ paddingBottom: selectedList.length > 0 ? barHeight + 24 : 24 }}>
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="grid-back" className="p-1.5 rounded-lg hover:bg-slate-100"><ArrowLeft className="h-5 w-5 text-slate-600" /></button>
          <div>
            <p className="text-[11px] font-bold tracking-wide" style={{ color: NAVY }}>{facility.name?.toUpperCase()}{sport ? ` · ${(SPORT_LABELS[sport] || sport).toUpperCase()}` : ''}</p>
            <p className="text-xs text-slate-500">Pick your slots</p>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 pt-4">
        {/* Sport step for multi-sport venues */}
        {sports.length > 1 && !sport && (
          <div data-testid="sport-step" className="grid grid-cols-2 gap-3 mb-4">
            {sports.map((s) => (
              <button key={s} data-testid={`sport-${s}`} onClick={() => setSport(s)}
                className="p-5 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-800 hover:border-[#0A2C93] flex flex-col items-center gap-2">
                <span className="text-3xl leading-none">{SPORT_ICONS[s] || '🏅'}</span>
                {SPORT_LABELS[s] || s}
              </button>
            ))}
          </div>
        )}

        {sport && (
          <>
            {/* Date strip + calendar picker — pinned below the header so the date can be changed after scrolling */}
            <div ref={dateStripRef} className="sticky top-[52px] z-40 bg-slate-50 -mx-4 px-4 py-2 flex items-stretch gap-2 mb-1" data-testid="date-bar">
              <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1" data-testid="date-strip">
                {Array.from({ length: 6 }).map((_, i) => {
                  const d = new Date(); d.setDate(d.getDate() + i); const ds = d.toISOString().slice(0, 10);
                  const on = ds === date;
                  return (
                    <button key={ds} data-testid={`date-${ds}`} onClick={() => { setSelected(new Map()); setPayMode('full'); setDate(ds); }}
                      className={`shrink-0 px-4 py-2 rounded-xl border-2 text-center text-sm ${on ? 'text-white border-transparent' : 'bg-white border-slate-200 text-slate-700'}`}
                      style={on ? { background: NAVY } : {}}>
                      <div className="font-bold">{d.toLocaleDateString('en-IN', { day: 'numeric' })}</div>
                      <div className="text-[10px]">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                    </button>
                  );
                })}
              </div>
              <div className="relative shrink-0">
                <button type="button" data-testid="date-picker-btn"
                  onClick={() => { const el = dateInputRef.current; if (el) { if (el.showPicker) el.showPicker(); else el.focus(); } }}
                  className={`h-full px-3 rounded-xl border-2 flex flex-col items-center justify-center ${date > (() => { const d = new Date(); d.setDate(d.getDate() + 5); return d.toISOString().slice(0, 10); })() ? 'text-white border-transparent' : 'bg-white border-slate-200 text-slate-600'}`}
                  style={date > (() => { const d = new Date(); d.setDate(d.getDate() + 5); return d.toISOString().slice(0, 10); })() ? { background: NAVY } : {}}>
                  <CalendarDays className="h-4 w-4" />
                  <span className="text-[9px] mt-0.5 font-semibold">More</span>
                </button>
                <input ref={dateInputRef} type="date" min={todayStr} max={maxDate} value={date}
                  onChange={(e) => { if (e.target.value) { setSelected(new Map()); setPayMode('full'); setDate(e.target.value); } }}
                  data-testid="date-picker-input"
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" tabIndex={-1} />
              </div>
            </div>

            {/* Pinned slim bar: format chips + column headers (stay fixed while rows scroll under),
                pinned directly beneath the sticky date bar above. */}
            {!loading && (
              <div className="sticky z-30 bg-slate-50 -mx-4 px-4 pt-1.5 pb-0" style={{ top: 52 + dateBarH }} data-testid="pinned-format-bar">
                {columns.length > 3 && (
                  <div className="flex gap-2 overflow-x-auto no-scrollbar mb-1.5" data-testid="format-chips">
                    {chips.map((c) => (
                      <button key={c} data-testid={`chip-${c}`} onClick={() => setChip(c)}
                        className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${chip === c ? 'text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
                        style={chip === c ? { background: ORANGE } : {}}>{c}</button>
                    ))}
                  </div>
                )}
                {/* column headers — horizontal scroll kept IN SYNC with the grid body below */}
                <div ref={headerScrollRef} onScroll={() => syncScroll(headerScrollRef.current, bodyScrollRef.current)}
                  className="overflow-x-auto no-scrollbar bg-white rounded-t-xl border border-b-0 border-slate-200" data-testid="grid-header-scroll">
                  <div className="min-w-max md:min-w-full">
                    <div className="flex">
                      <div className="w-14 md:w-20 shrink-0 sticky left-0 z-10 bg-white border-r border-slate-100" />
                      {visibleCols.map((c, i) => (
                        <div key={c.id} className="w-24 md:flex-1 md:min-w-0 shrink-0 px-1 py-1.5 text-center">
                          <p className="text-[11px] md:text-xs font-bold text-slate-800 leading-tight truncate">
                            {(chip !== 'All' && chip !== '11v11') ? `Court ${i + 1}` : c.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="bg-white rounded-b-xl border border-t-0 border-slate-200 overflow-hidden" data-testid="grid-skeleton">
                {Array.from({ length: 9 }).map((_, r) => (
                  <div key={r} className="flex border-b border-slate-50 last:border-0">
                    <div className="w-14 md:w-20 flex-shrink-0 py-3 px-2 border-r border-slate-100">
                      <div className="h-3 w-10 rounded bg-slate-100 animate-pulse" />
                    </div>
                    <div className="flex-1 flex gap-1 p-1.5">
                      {Array.from({ length: 4 }).map((__, c) => (
                        <div key={c} className="flex-1 h-10 rounded-md bg-slate-100 animate-pulse" style={{ animationDelay: `${(r + c) * 40}ms` }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="relative bg-white rounded-b-xl border border-t-0 border-slate-200 overflow-hidden">
                {/* Desktop: full grid (all columns). Mobile: pinned time col + horizontal scroll. */}
                <div ref={bodyScrollRef} onScroll={() => syncScroll(bodyScrollRef.current, headerScrollRef.current)}
                  className="overflow-x-auto no-scrollbar" data-testid="grid-scroll">
                  <div className="min-w-max md:min-w-full">
                    {/* rows */}
                    {times.map((time, idx) => {
                      // Cross-midnight orientation cue — for overnight facilities
                      // (5AM–2AM etc.) post-midnight slots (00:00, 00:30, 01:00, 01:30)
                      // appear on the SAME day's grid. Show a subtle divider + label
                      // at the boundary so late-night bookers aren't confused about
                      // the date.
                      const prev = idx > 0 ? times[idx - 1] : null;
                      const crossesMidnight = prev && timeToMin(prev) > 720 && timeToMin(time) < 360;
                      return (
                      <React.Fragment key={time}>
                        {crossesMidnight && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-200" data-testid="midnight-marker">
                            <span aria-hidden>🌙</span>
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                              Early morning — {new Date(`${nextDateStr}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        )}
                      <div ref={time === firstUpcoming ? upcomingRowRef : undefined} className="flex border-b border-slate-50 last:border-0">
                        <div className="w-14 md:w-20 shrink-0 sticky left-0 z-10 bg-white border-r border-slate-100 flex items-center justify-center">
                          <span className="text-[11px] font-semibold text-slate-500">{time}</span>
                        </div>
                        {visibleCols.map((c) => {
                          const { state, price } = cellState(time, c);
                          const key = `${time}|${c.id}`;
                          const sel = selected.has(key);
                          return (
                            <div key={key} className="w-24 md:flex-1 md:min-w-0 shrink-0 p-0.5">
                              <button data-testid={`cell-${time}-${c.id}`} data-state={sel ? 'selected' : state}
                                onClick={() => toggle(time, c)} disabled={state !== 'available' && !sel}
                                className={`w-full rounded-lg h-14 flex flex-col items-center justify-center text-[11px] transition-colors ${cellCls(state, sel)}`}
                                style={sel ? { background: NAVY } : {}}>
                                {state === 'booked' ? <span className="text-[9px]">Booked</span>
                                  : state === 'past' ? null
                                  : state === 'overlap' ? <span className="text-[9px] text-center leading-tight">Overlaps<br />selection</span>
                                  : (<><span className="font-semibold">₹{price?.toLocaleString('en-IN')}</span>
                                    <span className="text-[9px]" style={sel ? {} : { color: state === 'available' ? ORANGE : undefined }}>{sel ? 'Selected' : 'Available'}</span></>)}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                {/* mobile horizontal-scroll affordance */}
                <div className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent flex items-center justify-end">
                  <ChevronRight className="h-5 w-5 text-slate-300 animate-pulse" />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Grid docked bar — slim: accurate slot summary + Proceed to the summary page */}
      {selectedList.length > 0 && (
        <div ref={summaryRef} className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] z-40" data-testid="summary-bar">
          <div className="max-w-6xl mx-auto px-4 py-3">
            {holdLeft > 0 && (
              <div className="mb-2 text-[11px] font-semibold flex items-center gap-1" style={{ color: ORANGE }} data-testid="hold-countdown">
                <Clock className="h-3.5 w-3.5" /> Slots held — {mmss} left to pay
              </div>
            )}
            <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2" data-testid="selected-chips">
              {selectedList.map((s) => (
                <span key={`${s.time}|${s.courtId}`} className="shrink-0 text-[10px] bg-slate-100 rounded-full pl-2.5 pr-1 py-1 flex items-center gap-1">
                  {s.courtName} · {s.time}–{slotEnd(s.time)} · ₹{s.price?.toLocaleString('en-IN')}
                  <button data-testid={`remove-${s.time}-${s.courtId}`} onClick={() => removeSlot(s.time, s.courtId)}><X className="h-3 w-3 text-slate-400" /></button>
                </span>
              ))}
            </div>
            {selectedList.length < 2 && (
              <p className="text-[11px] text-orange-600 mb-2 font-medium" data-testid="min-slot-hint">Minimum booking is 1 hour — select at least 2 consecutive slots.</p>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-slate-500">{selectedList.length} slot{selectedList.length > 1 ? 's' : ''}</p>
                <p className="text-lg font-bold text-slate-900">₹{total.toLocaleString('en-IN')}</p>
              </div>
              <button data-testid="proceed-btn" onClick={goToSummary} disabled={selectedList.length < 2}
                className="text-white font-bold px-6 py-2.5 rounded-xl text-sm disabled:opacity-60 flex items-center gap-2" style={{ background: ORANGE }}>
                Proceed <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogin && <LoginModal onClose={() => { setShowLogin(false); setPendingProceed(false); }} onSuccess={() => { setShowLogin(false); }} />}
    </div>
  );
}
